import React, { Component, useEffect, useState } from "react";
import { AlertContext } from "../../components/AlertContext";
import { Box, List, ListItem, Typography, IconButton, Input, Skeleton, FormLabel, FormControl, Select, Option, Chip, Radio, Switch, CircularProgress } from "@mui/joy";
import { axiosInstance } from "../../helpers/AxiosConfiguration";
import { calculateAndConvertTime, retrieveDateStringFromISO, retrieveTimeStringFromISO } from "../../helpers/TimeConversionHelper";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Search } from '@mui/icons-material';
import PastReportsExpanded from "../../components/PastReportsExpanded";
import {
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
  } from "react-router-dom";
import { getBackground, getColor } from "../../helpers/GetColors";
import { GetFeedbackIcon } from "../../helpers/IconGeneration";
import { getClientUser, getUserByID, useFetchOrgUsers } from "../../helpers/UserAccounts";
import { mapStatusNames } from "../../components/FeedbackComponent";
import { getUserID } from "../../helpers/AuthBarrier";
import { Filter } from "lucide-react";
import FilterPopover from "../../components/modals/FilterDate";

function SelectUserID({forwardSelectedUserID, currentSelected, disabled, userRole}) {
    const [ selectedUserID, setSelectedUserID] = useState(getUserID());

    const {
        usersLoaded: individualsLoaded,
        loadingUsers: loadingIndividuals,
        errorUsers: errorIndividuals
    } = useFetchOrgUsers('individual');
    const [ individuals, setIndividuals] = useState([]);

    const onSelect = (event, newValue) => {
        setSelectedUserID(newValue);
        forwardSelectedUserID(newValue);
    }

    useEffect(() => {
        individualsLoaded && setIndividuals(individualsLoaded);
    }, [individualsLoaded])

    return <Select value={selectedUserID} onChange={onSelect} disabled={disabled || userRole === "individual"} defaultValue={currentSelected}>
        <List>
            <Option value={getUserID()}> Me </Option>
            {userRole !== "individual" && <>
                <ListItem>
                    <Typography level="body-xs"> Responders </Typography>
                </ListItem>
                {loadingIndividuals && <CircularProgress />}
                {individuals && individuals.map((item, index) => {
                    if (getUserID() === item["id"]) {
                        return <> </>;
                    }
                    return <Option value={item["id"]}> {item["full_name"]} </Option>
                })}
            </>}

        </List>
    </Select>

}


class PastReports extends Component {
    static contextType = AlertContext;
    constructor(props) {
        super(props);
        this.state = {
            visitsGenerated: [],
            fetchMessage: "",
            groupedVisits: {},
            dateFrom: '1 month',
            dateTo: 'current',
            showFilter: false,
            page: 1,
            totalVisits: 0,
            size: 10,
            favorites: {},
            selectedVisit: null,
            statusFilterValue: "-",
            user: null,
        };
        // Debounce search
        this.debounceFetch = null;

        this.setStatusToURL = this.setStatusToURL.bind(this);
        this.addSearchToURL = this.addSearchToURL.bind(this);
        this.addAssignedToURL = this.addAssignedToURL.bind(this);
        this.addUserIDToURL = this.addUserIDToURL.bind(this);
    }

    toggleFavorite = (itemId) => {
        this.setState(prevState => ({
            favorites: {
                ...prevState.favorites,
                [itemId]: !prevState.favorites[itemId]
            }
        }));
    }

    componentDidMount() {
        this.fetchVisits(this.state.page);
        this.getUser();
    }

    async getUser() {
        try {
            const response = await getClientUser();
            this.setState({ user: response })
        } catch (error) {
            console.log(error);
        }
    }

    cleanFilterAndSearchParams() {
        // Fetch current status of dependent params
        let user_id = this.props.router?.searchParams?.get("user_id");
        let assigned_to_me = this.props.router?.searchParams?.get("assigned_to_me");
        let status = this.props.router?.searchParams?.get("status");

        // If user_id is set to anything that's not the logged in user, then the user has set a value
        let user_id_paramed = false;
        if (user_id && user_id !== getUserID()) {
            if (this.state.user?.type === "individual") {
                user_id_paramed = false; // Shouldn't happen 
            } else {
                user_id_paramed = true;
            }
        }

        let assigned_to_me_paramed = false;
        if (!user_id_paramed && assigned_to_me && assigned_to_me !== "false") {
            assigned_to_me_paramed = true;
        }

        let status_paramed = false;
        if (!user_id_paramed && status) {
            status_paramed = true;
        } else if (user_id_paramed && status && this.state.user?.type !== "individual") {
            // status is only allowed to be "approved"
            status = "approved";
            this.setState({ statusFilterValue: "approved" })
            status_paramed = true;
        }

        this.props.router.setSearchParams(params => {
            if (user_id_paramed) {
                params.set("user_id", user_id);
            } else {
                params.delete("user_id");
            }

            if (assigned_to_me_paramed) {
                params.set("assigned_to_me", assigned_to_me);
            } else {
                params.delete("assigned_to_me");
            }

            if (status_paramed) {
                params.set("status", status)
            } else {
                params.delete("status");
            }

            return params;
        })
    }

    setFetchMessage(search_params) {
        let message = "Fetching reports, ";

        if ("user_id" in search_params) {
            if (search_params["user_id"] === getUserID()) {
                message += "made by me, "
            } else {
                message += `made by user with id "${search_params["user_id"]}", `
            }
        }

        if ("assigned_to_me" in search_params) {
            message += `that I have been tagged in, `
        }

        if ("status" in search_params && search_params["status"] !== "-") {
            message += `with report status "${search_params["status"]}", `
        }

        if ("text" in search_params && search_params["text"].length > 0) {
            message += `with title or patient name "${search_params["text"]}", `
        }
        
        message = message.replace(/[, ]+$/, '') + ".";
        return message;
    }

    addSearchToURL(event) {
        const newSearchParams = event.target.value;

        if (this.debounceFetch) {
            clearTimeout(this.debounceFetch);
        }

        this.debounceFetch = setTimeout(() => {
            this.props.router.setSearchParams(params => {
                params.set("text", newSearchParams);
                return params;
            })

            this.fetchVisits(this.state.page);
        }, 750);
    }

    addAssignedToURL(event) {
        if (this.debounceFetch) {
            clearTimeout(this.debounceFetch);
        }

        this.props.router.setSearchParams(params => {
            params.set("assigned_to_me", event.target.checked);
            return params;
        })

        this.cleanFilterAndSearchParams();
        this.fetchVisits(this.state.page);
    }

    addUserIDToURL(value) {
        this.props.router.setSearchParams(params => {
            params.set("user_id", value);
            return params;
        })

        this.cleanFilterAndSearchParams();
        this.fetchVisits(this.state.page);
    }

    setStatusToURL(event, newValue) {
        this.setState({
            statusFilterValue: newValue
        })

        if (this.debounceFetch) {
            clearTimeout(this.debounceFetch);
        }

        this.debounceFetch = setTimeout(() => {
            this.props.router.setSearchParams(params => {
                params.set("status", newValue);
                return params;
            })
            this.cleanFilterAndSearchParams();
            this.fetchVisits(this.state.page);
        }, 750);
    }


    fetchVisits = async (page, dateFrom = null, dateTo = null) => {
        try {
            const params = {
                'from_time': calculateAndConvertTime(dateFrom ? dateFrom.toISOString() : this.state.dateFrom),
                'to_time': calculateAndConvertTime(dateTo ? dateTo.toISOString() : this.state.dateTo),
                'sort': '-created_at',
                'page': page,
                'size': this.state.size,
                "text": this.props.router?.searchParams?.get("text") ?? '',
            }

            const status = this.props.router?.searchParams?.get("status")
            if (status && status !== "-") {
                params.status = status;
            }

            const assigned_to_me = this.props.router?.searchParams?.get("assigned_to_me");
            if (assigned_to_me) {
                params.assigned_to_me = assigned_to_me;
            }

            const user_id = this.props.router?.searchParams?.get("user_id") ?? getUserID();
            if (!params.assigned_to_me) {
                params.user_id = user_id;
            }

            const fetchMessage = this.setFetchMessage(params);


            this.setState({
                visitsGenerated: null,
                fetchMessage: fetchMessage,
            })

            const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
            await delay(1000);

            const response = await axiosInstance.get('/api/v1/visits', {
                params: params
            });

            const { items, total, page: currentPage, size } = response.data;

            const addName = {}
            const visits = []
            for (const item of items) {
                if (item["user_account_id"] in addName) {
                    item["full_name"] = addName[item["user_account_id"]]
                } else {
                    try {
                        const response = await getUserByID(item["user_account_id"])

                        item["full_name"] = response.full_name;
                        addName[item["user_account_id"]] = response.full_name;
                    } catch (error) {
                        console.log(error)
                    }
                }
                visits.push(item);
            }

            const groupedVisits = this.groupVisitsByDate(visits);

            this.setState({
                visitsGenerated: visits,
                groupedVisits: groupedVisits,
                fetchMessage: null,
                totalVisits: total,
                page: currentPage,
                size: size,
            });
        } catch (error) {
            this.context.addAlert("There was an error retrieving your reports", "danger");
        }
    }

    groupVisitsByDate(visits) {
        const grouped = {};
        visits.forEach(visit => {
            const date = new Date(`${visit.created_at}Z`);
            const header = this.getDateHeader(date);
            if (!grouped[header]) {
                grouped[header] = [];
            }
            grouped[header].push(visit);
        });
        return grouped;
    }

    getDateHeader(date) {
        const now = new Date();
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        const lastWeek = new Date(now);
        lastWeek.setDate(now.getDate() - 7);
        const lastMonth = new Date(now);
        lastMonth.setMonth(now.getMonth() - 1);
        const lastThreeMonths = new Date(now);
        lastThreeMonths.setMonth(now.getMonth() - 3);


        if (date.getDate() === now.getDate()) {
            return "1. Today";
        } else if (date.getDate() === yesterday.getDate()) {
            return "2. Yesterday";
        } else if (date >= lastWeek) {
            return "3. Last Week";
        } else if (date >= lastMonth) {
            return "4. Last Month";
        } else if (date >= lastThreeMonths) {
            return "5. Last 3 Months";
        } else {
            return "6. Older";
        }
    }

    formatDate(dateString) {
        const date = new Date(`${dateString}`);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    }

    handlePageChange = (direction) => {
        const newPage = direction === 'next' ? this.state.page + 1 : this.state.page - 1;
        if (newPage > 0 && newPage <= Math.ceil(this.state.totalVisits / this.state.size)) {
            this.fetchVisits(newPage);
        }
    }

    handleVisitClick = (visit) => {
        this.props.router.navigate(`/past-reports/${visit.id}`)
    }

    handleCloseVisit = () => {
        this.setState({ selectedVisit: null });
        this.fetchVisits(this.state.page);
    }

    render() {
        const totalPages = Math.ceil(this.state.totalVisits / this.state.size);

        const sortedHeaders = Object.keys(this.state.groupedVisits).sort();

        return (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <FilterPopover open={this.state.showFilter} setClose={() => { this.setState({ showFilter: false })}} raise_selections={(dateFrom, dateTo) => { 
                    this.setState({ dateFrom: dateFrom.toISOString(), dateTo: dateTo.toISOString() })
                    this.fetchVisits(this.state.page, dateFrom, dateTo)
                    }}/>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, gap: 2 }}>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Search by title or patient</FormLabel>
                        <Input size="sm" placeholder="Search" startDecorator={<Search />} sx={{p: "8px"}} onChange={this.addSearchToURL} defaultValue={this.props.router.searchParams.get("text")}/>
                    </FormControl>
                    <FormControl size="sm">
                        <FormLabel> Filter </FormLabel>
                        <IconButton sx={{ p: 1 }} onClick={() => {this.setState({ showFilter: true })}}>
                            <Filter />
                        </IconButton>
                    </FormControl>
                    <FormControl size="sm">
                        <FormLabel> Created by </FormLabel>
                        {this.state.user && <SelectUserID forwardSelectedUserID={(value) => {
                            this.addUserIDToURL(value)
                        }} disabled={false} userRole={this.state.user.type} currentSelected={this.props.router.searchParams.get("user_id")}/>}
                    </FormControl>
                    <FormControl size="sm">
                        <FormLabel> Assigned to me </FormLabel>
                        <Switch size="lg" sx={{ my: 1, gap: 1 }} onChange={this.addAssignedToURL} checked={this.props.router.searchParams.get("assigned_to_me") === "true"} disabled={this.props.router.searchParams.get("user_id")} endDecorator={this.props.router.searchParams.get("assigned_to_me") === "true" ? "On" : "Off"}/>
                    </FormControl>
                    <FormControl size="sm">
                        <FormLabel> Report status</FormLabel>
                        <Select
                        size="sm"
                        value={this.state.statusFilterValue}
                        placeholder="Filter by status"
                        slotProps={{ button: { sx: { whiteSpace: 'nowrap' } } }}
                        defaultValue={this.props.router?.searchParams?.get("status") ?? '-'}
                        onChange={this.setStatusToURL}
                        disabled={this.props.router.searchParams.get("user_id")}
                        >
                            <Option value="-"> - </Option>
                            <Option value="draft">Drafts</Option>
                            <Option value="prelim_check">Preliminary Check</Option>
                            <Option value="review">In Review</Option>
                            <Option value="denied">Denied</Option>
                            <Option value="approved">Approved</Option>
                        </Select>
                    </FormControl>
                    <FormControl size="sm">
                        <FormLabel>
                            {`${this.state.page}-${totalPages} of ${this.state.totalVisits} items`}
                        </FormLabel>
                        <Box sx={{display: "flex", padding: "2px"}}>
                            <IconButton 
                                onClick={() => this.handlePageChange('prev')} 
                                disabled={this.state.page === 1}
                            >
                                <ArrowBackIosNewIcon />
                            </IconButton>
                            <IconButton 
                                onClick={() => this.handlePageChange('next')} 
                                disabled={this.state.page === totalPages}
                            >
                                <ArrowForwardIosIcon />
                            </IconButton>
                        </Box>
                    </FormControl>
                </Box>
                <Box sx={{ px: 2, pb: 2, display: "flex", gap: 2, alignItems: "center"}}>
                    Filters Applied: 
                    <Chip>
                        From: {this.formatDate(calculateAndConvertTime(this.state.dateFrom))}
                    </Chip>
                    <Chip>
                        To: {this.formatDate(calculateAndConvertTime(this.state.dateTo))}
                    </Chip>
                </Box>
                {this.state.visitsGenerated ? <Box>
                    {!this.state.selectedVisit ? (
                        <List sx={{ flexGrow: 1, overflow: 'auto' }}>
                            {sortedHeaders.map((header) => (
                                <React.Fragment key={header}>
                                    <Box sx={{display: 'flex', p: 1, backgroundColor: "#EDF5FD"}}>
                                        <Box sx={{ width: "10%"}}>
                                            <Typography level="title-md" fontWeight={"bold"}> {header.slice(3)}</Typography>
                                        </Box>
                                        <Box sx={{ 
                                                textAlign: 'left',
                                                width: "20.5%",
                                            }}>
                                            <Typography level="title-md" fontWeight={"bold"}> Title </Typography>
                                        </Box>
                                        <Box sx={{ width: "15.5%" }}>
                                            <Typography level="title-md" fontWeight={"bold"}> Report Status </Typography>
                                        </Box>
                                        <Box sx={{ width: "15.5%", textAlign: 'left'}}>
                                            <Typography level="title-md" fontWeight={"bold"}>
                                                Created by
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'left', width: "15%" }}>
                                            <Typography level="title-md" fontWeight={"bold"}> Report Date </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'left', width: "15%" }}>
                                            <Typography level="title-md" fontWeight={"bold"}> Created at </Typography>
                                        </Box>
                                    </Box>
                                    {this.state.groupedVisits[header].map((item, index) => (
                                        <ListItem 
                                        key={item.id || index}
                                        sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            borderBottom: '1px solid #e0e0e0',
                                            py: 2,
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => this.handleVisitClick(item)}
                                        >
                                            <Box sx={{ 
                                                marginLeft: "10%",
                                                textAlign: 'left',
                                                width: "20%",
                                            }}>
                                                <Typography noWrap>
                                                    {(item.title !== null && item.title !== '' && item.title !== 'null') ? item.title : `Report ${retrieveDateStringFromISO(item.details.incident_occurred_at)}`}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ width: "15%" }}>
                                                <Chip variant="soft" sx={{backgroundColor: getBackground(item.status)}}>
                                                    <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                                        <GetFeedbackIcon status={item.status} />
                                                        <Typography color={getColor(item.status)}> {mapStatusNames(item.status)} </Typography>
                                                    </Box>
                                                </Chip>
                                            </Box>
                                            <Box sx={{width: "15%", textAlign: 'left'}}>
                                                <Typography level="body-sm">
                                                    {item.full_name}
                                                </Typography>
                                            </Box>
                                            <Typography sx={{ textAlign: 'left', width: "15%"}} noWrap>
                                                {this.formatDate(item.details.incident_occurred_at)}
                                            </Typography>
                                            <Typography sx={{ textAlign: 'left', width: "15%"}} noWrap>
                                                {this.formatDate(item.created_at)}
                                            </Typography>
                                        </ListItem>
                                    ))}
                                </React.Fragment>
                            ))}
                            {Object.keys(this.state.groupedVisits).length === 0 && (
                                <ListItem>No reports to display</ListItem>
                            )}
                        </List>
                    ) : (
                        <Box sx={{ flexGrow: 1, overflow: 'none', width: '100%'}}>
                            <PastReportsExpanded 
                                visit={this.state.selectedVisit}
                                edit={this.props.sort === 'draft'}
                                onClose={() => this.setState({ selectedVisit: null })}
                            />
                        </Box>
                    )}
                </Box> : this.state.fetchMessage ? <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", my: 8, gap: 3}}>
                    <CircularProgress size="lg"/>
                    <Typography level="body-lg"> {this.state.fetchMessage} </Typography>
                </Box> : <Skeleton />}
            </Box>
        );
    }
}

function withRouter(Component) {
    function ComponentWithRouterProp(props) {
        const location = useLocation();
        const navigate = useNavigate();
        const params = useParams();
        const [ searchParams, setSearchParams ] = useSearchParams();

        return (
          <Component
            {...props}
            router={{ location, navigate, params, searchParams, setSearchParams }}
          />
        );
      }
    
      return ComponentWithRouterProp;
}

export default withRouter(PastReports);